import { DEMO_BANKS, type DemoBankKey } from "./banks.ts";
import {
  DEMO_COMPANY_HOLDINGS_AG,
  DEMO_COMPANY_LABS_SP,
  DEMO_COMPANY_STUDIO_SRO,
  DEMO_COMPANY_TRADE_LTD,
  DEMO_COMPANY_VENTURES_GMBH,
  type DemoCompanyId,
} from "./identifiers.ts";

export type DemoAccountCurrency = "EUR" | "CZK" | "PLN" | "USD" | "GBP";

export type DemoAccount = {
  id: string;
  name: string;
  iban: string;
  accountNumber: string;
  currency: DemoAccountCurrency;
  balance: number;
  availableBalance: number;
  lastUpdate: string;
  bankKey: DemoBankKey;
  companyId: DemoCompanyId;
  // Free-form tag used for the "category" badge in the accounts table.
  category:
    | "Operating"
    | "Payroll"
    | "Tax Reserve"
    | "Savings"
    | "FX Hedge"
    | "Petty Cash"
    | "Treasury"
    | "Investment";
};

// Helper — keeps the table below readable. Generates a long-enough fake IBAN
// (correct country prefix) by hashing the seed string. Not check-digit valid
// but UI never validates.
function ibanFor(country: string, seed: string): string {
  const digits = Array.from(seed)
    .map((c) => c.charCodeAt(0))
    .join("")
    .replace(/[^0-9]/g, "")
    .padEnd(20, "7")
    .slice(0, 20);
  const grouped = digits.match(/.{1,4}/g)?.join(" ") ?? digits;
  return `${country}${(seed.length * 13).toString().padStart(2, "0").slice(-2)} ${grouped}`;
}

// Anchored to the real current time — the dashboard flags any balance whose
// balanceDateTime is not from TODAY as "Nesprávný zůstatek" (see
// isDerivedBalance in @dfp/react-components), so a fixed date goes stale.
const NOW_ISO_HR_OFFSET = (hours: number) =>
  new Date(Date.now() - hours * 3600_000).toISOString();

type Tpl = {
  suffix: string;
  category: DemoAccount["category"];
  currency: DemoAccountCurrency;
  bankKey: DemoBankKey;
  balance: number;
  availableDelta?: number;
  staleHours: number;
};

const HOLDINGS_AG: Tpl[] = [
  { suffix: "Treasury EUR", category: "Treasury", currency: "EUR", bankKey: "bawag", balance: 1_842_310.45, staleHours: 2 },
  { suffix: "Operating EUR", category: "Operating", currency: "EUR", bankKey: "bawag", balance: 384_215.6, availableDelta: -12_400, staleHours: 1 },
  { suffix: "Payroll EUR", category: "Payroll", currency: "EUR", bankKey: "erste", balance: 248_900, staleHours: 4 },
  { suffix: "FX Hedge USD", category: "FX Hedge", currency: "USD", bankKey: "erste", balance: 312_680.4, staleHours: 6 },
  { suffix: "Investment Reserve EUR", category: "Investment", currency: "EUR", bankKey: "raiffeisenAt", balance: 950_000, staleHours: 5 },
  { suffix: "Petty Cash EUR", category: "Petty Cash", currency: "EUR", bankKey: "revolut", balance: 8_410.7, staleHours: 1 },
  { suffix: "Revolut Business EUR", category: "Operating", currency: "EUR", bankKey: "revolut", balance: 64_180.25, availableDelta: -2_100, staleHours: 1 },
  { suffix: "Tax Reserve EUR", category: "Tax Reserve", currency: "EUR", bankKey: "raiffeisenAt", balance: 412_000, staleHours: 6 },
];

const STUDIO_SRO: Tpl[] = [
  { suffix: "Provozní účet CZK", category: "Operating", currency: "CZK", bankKey: "kb", balance: 4_287_654.32, availableDelta: -102_440, staleHours: 1 },
  { suffix: "Payroll CZK", category: "Payroll", currency: "CZK", bankKey: "kb", balance: 1_540_000, staleHours: 6 },
  { suffix: "FX Account EUR", category: "FX Hedge", currency: "EUR", bankKey: "fio", balance: 78_420.18, staleHours: 3 },
  { suffix: "Tax Reserve CZK", category: "Tax Reserve", currency: "CZK", bankKey: "csob", balance: 2_140_000, staleHours: 4 },
  { suffix: "Savings CZK", category: "Savings", currency: "CZK", bankKey: "csas", balance: 3_650_000, staleHours: 5 },
  { suffix: "Wise Business EUR", category: "Operating", currency: "EUR", bankKey: "wise", balance: 41_205.5, staleHours: 1 },
  { suffix: "Petty Cash CZK", category: "Petty Cash", currency: "CZK", bankKey: "fio", balance: 92_140, staleHours: 2 },
];

const LABS_SP: Tpl[] = [
  { suffix: "Operating PLN", category: "Operating", currency: "PLN", bankKey: "mbank", balance: 1_284_900, availableDelta: -41_800, staleHours: 1 },
  { suffix: "Payroll PLN", category: "Payroll", currency: "PLN", bankKey: "mbank", balance: 624_500, staleHours: 4 },
  { suffix: "Tax Reserve PLN", category: "Tax Reserve", currency: "PLN", bankKey: "mbank", balance: 380_000, staleHours: 4 },
  { suffix: "EUR Buffer", category: "FX Hedge", currency: "EUR", bankKey: "revolut", balance: 92_840.5, staleHours: 1 },
  { suffix: "Wise USD", category: "FX Hedge", currency: "USD", bankKey: "wise", balance: 38_104.6, staleHours: 6 },
  { suffix: "Savings PLN", category: "Savings", currency: "PLN", bankKey: "mbank", balance: 1_100_000, staleHours: 6 },
];

const VENTURES_GMBH: Tpl[] = [
  { suffix: "Operating EUR", category: "Operating", currency: "EUR", bankKey: "n26", balance: 218_400.15, availableDelta: -8_240, staleHours: 1 },
  { suffix: "Investment Pool EUR", category: "Investment", currency: "EUR", bankKey: "n26", balance: 1_420_000, staleHours: 5 },
  { suffix: "Tax Reserve EUR", category: "Tax Reserve", currency: "EUR", bankKey: "n26", balance: 184_000, staleHours: 5 },
  { suffix: "Payroll EUR", category: "Payroll", currency: "EUR", bankKey: "n26", balance: 88_540, staleHours: 4 },
  { suffix: "Revolut Business USD", category: "FX Hedge", currency: "USD", bankKey: "revolut", balance: 142_810.6, staleHours: 1 },
  { suffix: "Wise EUR", category: "Operating", currency: "EUR", bankKey: "wise", balance: 38_490.4, staleHours: 1 },
];

const TRADE_LTD: Tpl[] = [
  { suffix: "Operating GBP", category: "Operating", currency: "GBP", bankKey: "wise", balance: 184_900.6, availableDelta: -12_400, staleHours: 1 },
  { suffix: "Tax Reserve GBP", category: "Tax Reserve", currency: "GBP", bankKey: "wise", balance: 142_000, staleHours: 4 },
  { suffix: "Royalty Pool GBP", category: "Treasury", currency: "GBP", bankKey: "revolut", balance: 312_400, staleHours: 5 },
  { suffix: "EUR Settlement", category: "FX Hedge", currency: "EUR", bankKey: "revolut", balance: 78_240.9, staleHours: 1 },
  { suffix: "USD Settlement", category: "FX Hedge", currency: "USD", bankKey: "wise", balance: 64_180.2, staleHours: 1 },
  { suffix: "Savings GBP", category: "Savings", currency: "GBP", bankKey: "wise", balance: 250_000, staleHours: 6 },
];

function buildAccounts(
  companyId: DemoCompanyId,
  tpls: Tpl[],
  idPrefix: string,
): DemoAccount[] {
  return tpls.map((t, i) => {
    const bank = DEMO_BANKS[t.bankKey];
    const seed = `${idPrefix}-${t.suffix}-${i}`;
    return {
      id: `demo-acc-${idPrefix}-${i + 1}`,
      name: t.suffix,
      iban: ibanFor(bank.country, seed),
      accountNumber: seed.replace(/[^0-9]/g, "").padStart(10, "0").slice(0, 10),
      currency: t.currency,
      balance: t.balance,
      availableBalance: t.balance + (t.availableDelta ?? 0),
      lastUpdate: NOW_ISO_HR_OFFSET(t.staleHours),
      bankKey: t.bankKey,
      companyId,
      category: t.category,
    };
  });
}

export const DEMO_ACCOUNTS: DemoAccount[] = [
  ...buildAccounts(DEMO_COMPANY_HOLDINGS_AG, HOLDINGS_AG, "holdings"),
  ...buildAccounts(DEMO_COMPANY_STUDIO_SRO, STUDIO_SRO, "studio"),
  ...buildAccounts(DEMO_COMPANY_LABS_SP, LABS_SP, "labs"),
  ...buildAccounts(DEMO_COMPANY_VENTURES_GMBH, VENTURES_GMBH, "ventures"),
  ...buildAccounts(DEMO_COMPANY_TRADE_LTD, TRADE_LTD, "trade"),
];

export const DEMO_ACCOUNT_BY_ID = Object.fromEntries(
  DEMO_ACCOUNTS.map((a) => [a.id, a]),
) as Record<string, DemoAccount>;

export function accountsForCompany(companyId: DemoCompanyId): DemoAccount[] {
  return DEMO_ACCOUNTS.filter((a) => a.companyId === companyId);
}
